import { useMutation, useQueries, useQuery } from '@tanstack/react-query';

import {
  assignLabelToCategory,
  assignLabelToPost,
  createCategory,
  createLabels,
  deleteLabelToPost,
  getCategories,
  getCategory,
  getLabel,
  getLabels
} from '@/api/labels';
import { Labels, LabelsCategories } from '@/models/labels';

type onSuccessType = (data: any) => void;
type onErrorType = (error: any) => void;

type MutationCallbacks = {
  onSuccess?: onSuccessType;
  onError?: onErrorType;
};

export const useFetchLabels = (params: Labels.Params) =>
  useQuery({
    queryKey: ['labels', params],
    queryFn: async () => {
      const result = await getLabels(params);
      return result.data;
    },
    placeholderData: (previousData) => previousData
  });

export const useFetchLabel = (id: number) =>
  useQuery({
    queryKey: ['label', id],
    queryFn: async () => {
      const result = await getLabel(id);
      return result.data;
    }
  });

export const useCreateLabels = ({
  onSuccess,
  onError
}: MutationCallbacks = {}) =>
  useMutation({
    mutationFn: createLabels,
    onSuccess,
    onError
  });

type LabelToPostVariables = {
  id: number;
  postId: number;
  payload?: { categoryId?: number };
};

export const useAssignLabelToPost = ({
  onSuccess,
  onError
}: MutationCallbacks = {}) =>
  useMutation({
    mutationFn: ({ id, postId, payload = {} }: LabelToPostVariables) =>
      assignLabelToPost(id, postId, payload),
    onSuccess,
    onError
  });

export const useDeleteLabelToPost = ({
  onSuccess,
  onError
}: MutationCallbacks = {}) =>
  useMutation({
    mutationFn: ({ id, postId, payload = {} }: LabelToPostVariables) =>
      deleteLabelToPost(id, postId, payload),
    onSuccess,
    onError
  });

export const useFetchLabelsCategories = (params: LabelsCategories.Params) =>
  useQuery({
    queryKey: ['labelsCategories', params],
    queryFn: async () => {
      const result = await getCategories(params);
      return result.data;
    },
    placeholderData: (previousData) => previousData
  });

export const useFetchLabelsCategory = (categoryIds: number[], enabled = true) =>
  useQueries({
    queries: categoryIds.map((id) => ({
      queryKey: ['labelsCategory', id],
      queryFn: async () => {
        const result = await getCategory(id);
        return result.data;
      },
      enabled
    }))
  });

export const useCreateCategory = ({
  onSuccess,
  onError
}: MutationCallbacks = {}) =>
  useMutation({
    mutationFn: createCategory,
    onSuccess,
    onError
  });

export const useAssignLabelToCategory = ({
  onSuccess,
  onError
}: MutationCallbacks = {}) =>
  useMutation({
    mutationFn: ({
      categoryId,
      labelId
    }: {
      categoryId: number;
      labelId: number;
    }) => assignLabelToCategory(categoryId, labelId),
    onSuccess,
    onError
  });
